import React, { useState } from "react";
import { motion } from "framer-motion";
import { Fan, ArrowRight } from 'lucide-react';
import { features } from "./data";

const FeaturesSection = () => {
  // Images haute qualité pour le fond des cartes
  const cardImages = ['/c2.jpg', '/c3.jpg', '/c4.jpg', '/c5.jpg'];

  return (
    <section className="py-24 relative overflow-hidden">

      {/* Background global subtil pour ne pas interférer avec le reste du site */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-0 left-1/4 w-[500px] h-[500px] bg-blue-500/10 rounded-full blur-[120px] opacity-20" />
        <div className="absolute bottom-0 right-1/4 w-[500px] h-[500px] bg-cyan-500/10 rounded-full blur-[120px] opacity-20" />
      </div>

      <div className="container mx-auto px-4 relative z-10">

        {/* En-tête de section Premium */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-20"
        >
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-blue-500/30 bg-blue-500/10 backdrop-blur-md mb-6 shadow-[0_0_15px_rgba(59,130,246,0.2)]">
            <Fan className="w-4 h-4 text-blue-400" />
            <span className="text-blue-300 text-xs font-bold tracking-[0.2em] uppercase">Excellence & Innovation</span>
          </div>

          <h2 className="text-4xl sm:text-5xl md:text-7xl font-bold text-white mb-6 md:mb-8 leading-tight tracking-tight">
            Pourquoi
            <span className="bg-gradient-to-r from-blue-400 via-cyan-300 to-blue-600 bg-clip-text text-transparent px-2 sm:px-4">
              Nous Choisir ?
            </span>
          </h2>

          <p className="text-gray-400 text-lg md:text-xl max-w-2xl mx-auto font-light leading-relaxed">
            Une approche technologique sans compromis pour propulser votre vision vers de nouveaux sommets.
          </p>
        </motion.div>

        {/* Grille Masterpiece */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-8">
          {features.map((item, idx) => (
            <FeatureCard key={idx} item={item} idx={idx} image={cardImages[idx]} />
          ))}
        </div>

        {/* Footer de section */}
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          transition={{ delay: 0.8 }}
          className="mt-20 flex justify-center"
        >
          <div className="h-px w-24 bg-gradient-to-r from-transparent via-blue-500/50 to-transparent" />
        </motion.div>

      </div>
    </section>
  );
};

const FeatureCard = ({ item, idx, image }) => {
  const [isHovered, setIsHovered] = useState(false);

  return (
    <motion.div
      initial={{ opacity: 0, y: 50 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-50px" }}
      transition={{ delay: idx * 0.1, duration: 0.7, ease: [0.21, 0.47, 0.32, 0.98] }}
      className="relative h-[400px] sm:h-[440px] md:h-[480px] w-full group rounded-[2rem] overflow-hidden" // Hauteur adaptative
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* Container principal avec bordure subtile */}
      <div className="absolute inset-0 rounded-[2rem] border border-white/10 group-hover:border-blue-500/50 transition-colors duration-500 z-20 pointer-events-none" />

      {/* IMAGE DE FOND */}
      <div className="absolute inset-0 z-0 bg-gray-900">
        <motion.div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url('${image || '/c2.jpg'}')` }}
          animate={{
            scale: isHovered ? 1.1 : 1,
            filter: isHovered ? "brightness(0.8) blur(0px)" : "brightness(0.6) blur(2px)"
          }}
          transition={{ duration: 0.7 }}
        />
        {/* Overlay Gradient puissant pour lisibilité texte */}
        <div className="absolute inset-0 bg-gradient-to-t from-[#020617] via-[#020617]/80 to-transparent opacity-90" />

        {/* Effet de couleur au hover */}
        <div className={`absolute inset-0 bg-gradient-to-tr ${item.color || "from-blue-600/20 to-cyan-500/20"} opacity-0 group-hover:opacity-40 transition-opacity duration-500 mix-blend-overlay`} />
      </div>

      {/* CONTENU - Positionné en bas */}
      <div className="relative z-10 h-full flex flex-col justify-end p-8">

        {/* Icone Flottante */}
        <div className="absolute top-8 right-8">
          <div className="w-12 h-12 rounded-full bg-white/5 border border-white/10 backdrop-blur-md flex items-center justify-center group-hover:bg-blue-500 group-hover:border-blue-400 transition-all duration-500 shadow-lg">
            <item.icon className={`w-5 h-5 text-gray-300 group-hover:text-white transition-colors duration-300`} />
          </div>
        </div>

        {/* Numéro décoratif */}
        <span className="text-6xl font-black text-white/5 absolute top-4 left-6 pointer-events-none select-none">
          0{idx + 1}
        </span>

        {/* Contenu Texte */}
        <div className="transform transition-transform duration-500 group-hover:-translate-y-2">
          <h3 className="text-2xl font-bold text-white mb-3 group-hover:text-blue-200 transition-colors leading-tight">
            {item.title}
          </h3>

          <p className="text-gray-400 text-sm leading-relaxed mb-6 opacity-80 group-hover:opacity-100 transition-opacity duration-300 line-clamp-3">
            {item.desc}
          </p>

          {/* Bouton "En savoir plus" simulé */}
          <div className="flex items-center gap-2 text-blue-400 font-medium text-sm opacity-0 group-hover:opacity-100 transform translate-y-4 group-hover:translate-y-0 transition-all duration-500">
            <span>Découvrir</span>
            <ArrowRight className="w-4 h-4" />
          </div>
        </div>

        {/* Barre de progression bottom */}
        <div className="absolute bottom-0 left-0 right-0 h-1 bg-white/5">
          <motion.div
            className="h-full bg-gradient-to-r from-blue-500 to-cyan-400"
            initial={{ width: "0%" }}
            animate={{ width: isHovered ? "100%" : "0%" }}
            transition={{ duration: 0.5, ease: "easeOut" }}
          />
        </div>
      </div>
    </motion.div>
  );
};

export default FeaturesSection;
